"use client";

import {
  AddressWithLocation,
  defaultAddressWithLocation,
} from "@/types/location";
import PlaceAutocomplete from "../PlaceAutocomplete";
import { useEffect, useState } from "react";
import { MdCancel, MdEdit, MdSave } from "react-icons/md";
import { cn } from "@/lib/utils";

type Props =
  | {
      /**
       * This specifies the way in which the location form field is used.
       *
       * "change" Marks the form as a change form, meaning every
       * selected Location is applied immediately by calling the setLocation function.
       *
       * "submit" will only use the setLocation function after the submit button has been pressed.
       *
       * "edit" will only make the field editable after pressing the edit button. The setLocation function is called after the edit button has been pressed, followed by the submit button.
       *
       * "view" will only show the address, and will not allow the user to edit it.
       */
      formType: "change" | "edit" | "submit";
      name: string;
      label?: string;
      placeholder: string;
      error?: { show: boolean; text: string };
      setLocation: (address: AddressWithLocation) => void;
      onClear?: () => void;
      initialValue?: AddressWithLocation;
      //isClearable?: boolean;
      formIcon?: React.ReactNode;
      buttonClass?: string;
      className?: string;
      type?: "street_address" | "locality";
      design: "old" | "new";
      "data-sentry-unmask"?: boolean;
    }
  | {
      formType: "view";
      name: string;
      label?: string;
      initialValue: AddressWithLocation;
      formIcon?: React.ReactNode;
      buttonClass?: string;
      className?: string;
      design: "old" | "new";
      "data-sentry-unmask"?: boolean;
    };

export default function LocationFormField(props: Props) {
  const [editable, setEditable] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<AddressWithLocation>(
    props.initialValue ?? defaultAddressWithLocation,
  );

  useEffect(() => {
    if (props.initialValue) {
      setCurrentLocation(props.initialValue);
    }
  }, [props.initialValue]);

  function setLocation(location: AddressWithLocation): void {
    if (props.formType === "change") {
      setCurrentLocation(location);
      props.setLocation(location);
    } else if (props.formType === "edit" && editable) {
      setCurrentLocation(location);
      props.setLocation(location);
    } else if (props.formType === "submit") {
      setCurrentLocation(location);
    }
  }

  function handleSubmit(): void {
    if (props.formType === "edit" && !editable) {
      setEditable(true);
    } else if (props.formType === "edit") {
      props.setLocation(currentLocation);
    } else if (props.formType === "submit") {
      props.setLocation(currentLocation);
    }
  }

  function handleCancel(): void {
    setEditable(false);
  }

  return (
    <div className={cn(`form-group`, props.className)}>
      <div className={`input-group`}>
        {props.label && <label htmlFor={props.name}>{props.label}</label>}
        {props.formIcon && (
          <span className="icon-container">{props.formIcon}</span>
        )}
        <div className={`auto-complete-container`}>
          {props.formType === "view" ||
          (props.formType === "edit" && !editable) ? (
            <div
              className={
                "placeAutocomplete " +
                (props.design === "old" ? "oldDesign" : "newDesign")
              }
            >
              <input
                id={props.name}
                value={currentLocation.address}
                data-sentry-unmask={props["data-sentry-unmask"]}
                readOnly={true}
              />
            </div>
          ) : (
            <PlaceAutocomplete
              className={
                "placeAutocomplete " +
                (props.design === "old" ? "oldDesign" : "newDesign")
              }
              name={props.name}
              placeholder={props.placeholder}
              type={props.type}
              setLocation={setLocation}
              onClear={props.onClear}
              useAddressFields={false}
              initialValue={props.initialValue}
              data-sentry-unmask={props["data-sentry-unmask"]}
            />
          )}
        </div>
        {props.formType !== "change" && props.formType !== "view" && (
          <div className="options">
            {editable && (
              <button className="pop-button" onClick={handleCancel}>
                <div className="icon">
                  <MdCancel />
                </div>
              </button>
            )}
            <button
              className={`pop-button ${props.buttonClass ?? ""}`}
              onClick={handleSubmit}
            >
              <div className="icon">
                {props.formType === "submit" ||
                (props.formType === "edit" && editable) ? (
                  <MdSave />
                ) : (
                  <MdEdit />
                )}
              </div>
            </button>
          </div>
        )}
      </div>
      {props.formType != "view" && props.error && props.error.show && (
        <span className="form-error">{props.error.text}</span>
      )}
    </div>
  );
}
